import { Injectable, InjectionToken, inject } from '@angular/core';
import { Params, Router } from '@angular/router';

/** The meta-app route hosting the workspace ("Meine Inhalte"). */
export const BD_WORKSPACE_PATH = '/home/content';

/**
 * Where this app's "Im Ordner öffnen" links lead.
 *
 * - **Not provided** (meta-app and friends): the workspace is an in-app route.
 * - **`{ externalOrigin: 'https://…' }`**: the app has no workspace of its own
 *   (the standalone Klia app), so workspace links become absolute links onto
 *   that origin and open in a new tab.
 * - **`{ externalOrigin: null }`**: no workspace and no origin to link to —
 *   workspace links are hidden rather than landing on a not-found page.
 *
 * The object wrapper is deliberate: an optional `inject` yields `null` both
 * for "not provided" and for "provided as null", which would merge the first
 * and last case.
 */
export interface BdWorkspaceLocation {
  externalOrigin: string | null;
}

export const BD_WORKSPACE_LOCATION = new InjectionToken<BdWorkspaceLocation>(
  'BD_WORKSPACE_LOCATION',
);

/**
 * Builds and follows links into the workspace, honouring
 * {@link BD_WORKSPACE_LOCATION}. Callers pass the workspace's query params
 * (`tab`, `shareableId`, `folderId`, …); the receiver resolves the rest.
 */
@Injectable({ providedIn: 'root' })
export class BdWorkspaceLinkService {
  private readonly _router = inject(Router);
  private readonly _location = inject(BD_WORKSPACE_LOCATION, {
    optional: true,
  });

  /** Whether the workspace can be reached from this app at all. */
  public readonly available =
    this._location === null || this._location.externalOrigin !== null;

  /** Whether workspace links leave this app (and should open a new tab). */
  public readonly external = !!this._location?.externalOrigin;

  /** The link as an `href`: app-relative in-app, absolute when external. */
  public href(queryParams: Params): string {
    const path = this._router.serializeUrl(
      this._router.createUrlTree([BD_WORKSPACE_PATH], { queryParams }),
    );
    const origin = this._location?.externalOrigin;
    return origin ? `${origin.replace(/\/+$/, '')}${path}` : path;
  }

  /** Follows the link: router navigation in-app, a new tab when external. */
  public open(queryParams: Params): void {
    if (!this.available) {
      return;
    }
    if (!this.external) {
      void this._router.navigate([BD_WORKSPACE_PATH], { queryParams });
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(this.href(queryParams), '_blank', 'noopener,noreferrer');
    }
  }
}
